import { PencilIcon, XIcon } from "@heroicons/react/outline";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/solid";
import { format } from "date-fns";
import { Field, Formik } from "formik";
import _get from "lodash/get";
import _sortBy from "lodash/sortBy";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import {
  getCompanySecretaryDocumentData,
  getDocumentData,
} from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm/documentData";
import { INDIVIDUALS } from "components/PLCTABS/SFLdata/config/constants";
import CommentsList from "components/lib/Global/CommentsList";
import CommentsPopup from "components/lib/Global/CommentsPopup";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import ReactTable from "components/lib/Shared/ReactTable";
import Tabs from "components/lib/Shared/Tabs";
import Tag from "components/lib/Shared/Tag";
import { Constants } from "config/constants";
import { useToast } from "hooks/useToast";
import { addComment, editComment, getComments } from "state/slices/comment";
import { invalidateFields, resolveFields } from "state/slices/resolveField";
import { updateCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import {
  getBeneficialOwnershipFormFields,
  getFormFields,
  getFullName,
} from "utils";
import { handleFilesUpload } from "utils/filesUtils";
import ChangeDocumentSignedUnsigned from "./ChangeDocumentSignedUnsigned";
import ChangeDocuments from "./ChangeDocuments";
import ESignature from "./ESignature";
import StakeholderTable from "./StakeholderTable";
import Button from "components/lib/Shared/Button";
import { updateTransaction } from "state/slices/transaction";

function StakeholderSubRow({
  visibleColumns,
  row,
  currentTab,
  initialRows,
  handleBackBtn,
  seniorJointShareholder,
  isSeniorJSReportable,
  isChangesSignature,
  ...restProps
}) {
  const { toast } = useToast(5000);
  const { companyId, trxId } = useParams();
  const history = useHistory();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const dispatch = useDispatch();
  const isViewMode =
    history.location.pathname.split("/").pop() === "view" ||
    restProps.isViewMode;
  const isReviewerMode =
    history.location.pathname.split("/").pop() === "review";
  const isSignatureMode =
    history.location.pathname.split("/").pop() === "signature";
  const isCompanyDashboard =
    history.location.pathname.split("/").pop() === "dashboard";
  const { comments } = useSelector((state) => state.commentsSlice);
  const currentUser = useSelector((state) => state.userSlice);
  const { beneficialOwners } = useSelector(
    (state) => state.beneficialOwnersTableSlice
  );
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { officials, transactions } = transactionUnderReview.items;
  const originalCompanyOfficials = officials;
  const stakeholderComments = comments.filter(
    (comment) =>
      comment?.officialId &&
      comment.officialId === row.original._id &&
      comment?.tabName &&
      comment.tabName === currentTab
  );

  const appointerComments = stakeholderComments.filter(
    (comment) => comment.formName === "appointers"
  );

  const generalInformationComments = stakeholderComments.filter(
    (comment) =>
      comment.formName === "identificationDetails" ||
      comment.formName === "names" ||
      comment.formName === "isDirector"
  );
  const residentialDetailsComments = stakeholderComments.filter(
    (comment) =>
      comment.formName === "postalAddress" ||
      comment.formName === "electronicAddress" ||
      comment.formName === "residentialAddressDetails" ||
      comment.formName === "isNonResident"
  );
  const officialShareAllocationComments = stakeholderComments.filter(
    (comment) => comment.formName === "officialShareAllocation"
  );
  const officialBeneficialOwnershipComments = stakeholderComments.filter(
    (comment) => comment.formName === "beneficialOwnershipForm"
  );
  const fileUploadComments = stakeholderComments.filter(
    (comment) => comment.formName === "uploadDocuments"
  );
  const changeDocumentsComments = comments.filter(
    (comment) => comment.formName === "uploadChangeDocuments"
  );
  const { company } = useSelector((state) => state.companySlice);

  const showOfficialShareAllocationTab =
    row.original?.sharesAllotted?.length > 0;
  const showBOTab = row.original?.beneficialOwnershipForm;

  const tabs = useMemo(() => {
    let tempTabs = [];

    let isSeniorJSReportable = false;
    if (seniorJointShareholder?.beneficialOwnershipForm) {
      const {
        directPercentShareholding,
        directPercentVotingRights,
        directRightRemoveDirector,
        directCompanyControlRight,
      } = seniorJointShareholder?.beneficialOwnershipForm;
      isSeniorJSReportable =
        directPercentShareholding >= 10 ||
        directPercentVotingRights >= 10 ||
        directRightRemoveDirector === "yes" ||
        directCompanyControlRight === "yes";
    }
    if (row?.original.cessationDate) {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
      ];
    } else if (
      ["Director", "Authorized Person", "Company Secretary"].includes(
        row.original.designation
      )
    ) {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
        {
          id: "residentialDetails",
          label: "Address details",
          commentsCount: residentialDetailsComments?.length,
        },
      ];
    } else if (row.original.designation === "Joint Shareholder") {
      if (
        seniorJointShareholder &&
        row.original._id === seniorJointShareholder._id
      ) {
        tempTabs = [
          {
            id: "generalInformation",
            label: "General Information",
            commentsCount: generalInformationComments?.length,
          },
          {
            id: "residentialDetails",
            label: "Address Details",
            commentsCount: residentialDetailsComments?.length,
          },
          {
            id: "officialShareAllocation",
            label: "Share information",
            commentsCount: officialShareAllocationComments?.length,
          },
          {
            id: "officialBeneficialOwnership",
            label: "Beneficial ownership",
            commentsCount: officialBeneficialOwnershipComments?.length,
          },
        ];
      } else {
        tempTabs = [
          {
            id: "generalInformation",
            label: "General Information",
            commentsCount: generalInformationComments?.length,
          },
          row.original.isDirector === "Yes" || isSeniorJSReportable
            ? {
              id: "residentialDetails",
              label: "Address Details",
              commentsCount: residentialDetailsComments?.length,
            }
            : null,
        ];
      }
    } else {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
        {
          id: "residentialDetails",
          label: "Address details",
          commentsCount: residentialDetailsComments?.length,
        },
        showOfficialShareAllocationTab
          ? {
            id: "officialShareAllocation",
            label: "Share information",
            commentsCount: officialShareAllocationComments?.length,
          }
          : null,
        showBOTab
          ? {
            id: "officialBeneficialOwnership",
            label: "Beneficial ownership",
            commentsCount: officialBeneficialOwnershipComments?.length,
          }
          : null,
      ];
    }
    if (row.original.designation === "Alternate Director") {
      tempTabs.unshift({
        id: "appointer",
        label: "Appointer",
      });
    }
    if (currentTab === "beneficialOwnership") {
      tempTabs = [
        {
          id: "generalInformation",
          label: "General information",
          commentsCount: generalInformationComments?.length,
        },
        {
          id: "residentialDetails",
          label: "Address details",
          commentsCount: residentialDetailsComments?.length,
        },
        {
          id: "officialBeneficialOwnership",
          label: "Beneficial ownership",
          commentsCount: officialBeneficialOwnershipComments?.length,
        },
      ];
    }
    if (!row.original?.isShareCapitalOfficial) {
      tempTabs.push({
        id: "uploadDocuments",
        label: "File Uploads",
        commentsCount: fileUploadComments?.length,
      });
    }
    if (isChangesSignature) {
      tempTabs.push({
        id: "changedDocumentsSignature",
        label: "Change Documents (Signature)",
        commentsCount: 0,
      });
    } else if (
      !isChangesSignature &&
      row?.original?.uploadChangeDocuments &&
      Object.values(row?.original?.uploadChangeDocuments).length > 0
    ) {
      tempTabs.push({
        id: "changedDocuments",
        label: "Change Documents",
        commentsCount: 0,
      });
      // Add E-Signature tab if there are envelopes
      if (row?.original?.envelopes?.length > 0) {
        tempTabs.push({
          id: "eSignature",
          label: "E-Signature",
        });
      }
    }

    return tempTabs.filter(Boolean);
  }, [
    row.original.designation,
    currentTab,
    generalInformationComments?.length,
    residentialDetailsComments?.length,
    fileUploadComments?.length,
    showOfficialShareAllocationTab,
    officialShareAllocationComments?.length,
    showBOTab,
    officialBeneficialOwnershipComments?.length,
    seniorJointShareholder,
  ]);

  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  const goToPrevStep = () => {
    if (selectedTabIndex === 0 && handleBackBtn) handleBackBtn();
    else setSelectedTabIndex(selectedTabIndex - 1);
  };

  const goToNextStep = () => {
    setSelectedTabIndex(selectedTabIndex + 1);
  };

  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            officialId: row.original._id,
            tabName: currentTab,
          },
        })
      );
    }
  };

  useEffect(() => {
    const tempSelectedTab = tabs[selectedTabIndex];
    if (tempSelectedTab) setSelectedTab(tempSelectedTab);
  }, [tabs, selectedTabIndex]);

  useEffect(() => {
    if (isChangesSignature) {
      setSelectedTabIndex(
        tabs.findIndex((tab) => tab.id === "changedDocumentsSignature")
      );
    }
  }, [isChangesSignature, tabs]);


  const isFirstStep = selectedTabIndex === 0;
  const isLastStep = tabs.length - 1 === selectedTabIndex;

  let dateOfBirth = _get(row.original.identificationDetails, "dateOfBirth");
  if (dateOfBirth) dateOfBirth = format(new Date(dateOfBirth), "yyyy/MM/dd");
  else dateOfBirth = "N/A";

  // approve/disapprove allotted shares to official
  const [isLoading, setIsLoading] = useState(false);
  const [updatingOfficial, setUpdatingOfficial] = useState(false)
  const [selectedRows, setSelectedRow] = useState([]);
  const [allFieldsResolved, setAllFieldsResolved] = useState(false);
  const [formData, setFormData] = useState({});
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const isChangeOfParticulars = params.get("changeOfParticulars");
  const isCessation = params.get("cessation");
  const isAppointment = params.get("appointment");
  const [formError, setFormError] = useState([]);
  const formId = _get(row.original, "_id");
  const resolvedFields = _get(row.original, "resolvedFields", []);
  const [transaction, setTransaction] = useState(null);

  const fieldsName = row.original?.sharesAllotted
    ?.map((share) => share.classOfShares)
    .filter(Boolean); // all the shares category names

  useEffect(() => {
    setAllFieldsResolved(
      fieldsName?.every((element) => resolvedFields.includes(element))
    );
  }, [fieldsName, resolvedFields]);
  useEffect(() => {
    if (formData.consentFormForAuthorizedPerson) {
      setFormError([]);
    }
  }, [formData]);

  const jointShareholderForReview = originalCompanyOfficials.filter(
    (official) =>
      row.original?.JSGroupID &&
      official?.JSGroupID === row.original?.JSGroupID &&
      row.original.idType === "Joint Shareholder"
  );

  if (
    jointShareholderForReview.length > 0 &&
    row.original?.JSGroupID &&
    row.original.idType === "Joint Shareholder"
  ) {
    return (
      <tr className="bg-[#EDF0F5]">
        <td colSpan={visibleColumns.length} className="p-9">
          <StakeholderTable
            data={_sortBy(jointShareholderForReview, ["JSGroupOrder"])}
            currentTab={currentTab}
            isJointShareholderTable
          />
        </td>
      </tr>
    );
  }

  const officialIndex =
    currentTab === "stakeholderTab"
      ? originalCompanyOfficials.findIndex(
        (official) => official._id === row.original._id
      )
      : beneficialOwners.findIndex(
        (official) => official._id === row.original._id
      );
  const officialSliceName =
    currentTab === "stakeholderTab"
      ? `transactionSlice.transactionUnderReview.items.officials.${officialIndex}`
      : `transactionSlice.transactionUnderReview.items.beneficialOwners.${officialIndex}`;
  const companyOfficialEffectKey = "updateTransactionUnderReview";

  const handleResolveBtnClicked = async (fieldName) => {
    // approve field
    const commentsToBeResolved = officialShareAllocationComments?.filter(
      (comment) => comment.fieldName === fieldName && !comment.isResolved
    );
    const commentsToBeResolvedIds = commentsToBeResolved?.map(({ _id }) => _id);
    setIsLoading(true);
    setSelectedRow((selectedRows) => [...selectedRows, fieldName]);
    const promise = await dispatch(
      resolveFields({
        data: {
          formId,
          formName:
            currentTab === "stakeholderTab"
              ? INDIVIDUALS.includes(row?.original?.idType)
                ? "individualOfficial"
                : "corporateOfficial"
              : "beneficialOwner",
          fieldName,
          resolvedFields: [fieldName],
          commentsToBeResolvedIds,
        },
        updateEffectKey: companyOfficialEffectKey,
        updateEffectPath: officialSliceName,
      })
    );
    if (commentsToBeResolvedIds.length > 0) {
      const query = { companyId: commentsToBeResolved[0]?.company };
      if (isReviewerMode) query.createdBy = currentUser?._id;
      await dispatch(getComments(query));
    }
    setIsLoading(false);
    setSelectedRow((selectedRows) => [
      ...selectedRows.filter(
        (rowName) => rowName !== promise.meta.arg.data.fieldName
      ),
    ]);
  };

  const handleInvalidateBtnClicked = async (fieldName) => {
    // disapprove field
    setIsLoading(true);
    setSelectedRow((selectedRows) => [...selectedRows, fieldName]);
    const promise = await dispatch(
      invalidateFields({
        data: {
          formId,
          formName:
            currentTab === "stakeholderTab"
              ? INDIVIDUALS.includes(row?.original?.idType)
                ? "individualOfficial"
                : "corporateOfficial"
              : "beneficialOwner",
          fieldName,
          invalidFields: [fieldName],
        },
        updateEffectKey: companyOfficialEffectKey,
        updateEffectPath: officialSliceName,
      })
    );
    setIsLoading(false);
    setSelectedRow((selectedRows) => [
      ...selectedRows.filter(
        (rowName) => rowName !== promise.meta.arg.data.fieldName
      ),
    ]);
  };

  const handleAllFieldsStatus = async (e) => {
    const commentsToBeResolved = officialShareAllocationComments?.filter(
      (comment) => !comment.isResolved
    );
    const commentsToBeResolvedIds = commentsToBeResolved?.map(({ _id }) => _id);
    setIsLoading(true);
    if (e.target.checked) {
      // resolve/approve all the form fields
      const unresolvedFields = fieldsName.filter(
        (fieldName) => !resolvedFields.includes(fieldName)
      );
      setSelectedRow((selectedRows) => [...selectedRows, ...unresolvedFields]);
      await dispatch(
        resolveFields({
          data: {
            formId,
            formName:
              currentTab === "stakeholderTab"
                ? INDIVIDUALS.includes(row?.original?.idType)
                  ? "individualOfficial"
                  : "corporateOfficial"
                : "beneficialOwner",
            resolvedFields: unresolvedFields,
            commentsToBeResolvedIds,
          },
          updateEffectKey: companyOfficialEffectKey,
          updateEffectPath: officialSliceName,
        })
      );
      if (commentsToBeResolvedIds.length > 0) {
        const query = { companyId: commentsToBeResolved[0]?.company };
        if (isReviewerMode) query.createdBy = currentUser?._id;
        await dispatch(getComments(query));
      }
      setIsLoading(false);
      setSelectedRow([]);
    } else {
      // invalidate/disapprove all the form fields
      const resolvedFieldsSlice = fieldsName.filter((fieldName) =>
        resolvedFields.includes(fieldName)
      );
      setSelectedRow((selectedRows) => [
        ...selectedRows,
        ...resolvedFieldsSlice,
      ]);
      await dispatch(
        invalidateFields({
          data: {
            formId,
            formName:
              currentTab === "stakeholderTab"
                ? INDIVIDUALS.includes(row?.original?.idType)
                  ? "individualOfficial"
                  : "corporateOfficial"
                : "beneficialOwner",
            invalidFields: resolvedFieldsSlice,
          },
          updateEffectKey: companyOfficialEffectKey,
          updateEffectPath: officialSliceName,
        })
      );
      setSelectedRow([]);
    }
    setIsLoading(false);
  };

  const officialShareAllocationColumns = [
    {
      Header: "Class of shares",
      Cell: ({ row }) => row.original.classOfShares,
    },
    { Header: "Number of Shares", accessor: "allottedShares" },
    {
      Header: "Value Of Shares",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.sharesValue}</Tag>
        </div>
      ),
    },
    { Header: "Paid Up Shares", accessor: "paidUpShares" },
    {
      Header: "Premium Paid",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.sharePremium}</Tag>
        </div>
      ),
    },
    {
      Header: "Nominal Value",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.nominalValue}</Tag>
        </div>
      ),
    },
    {
      Header: "Voting Rights",
      Cell: ({ row }) => row.original.votingRights,
    },
  ];

  if (!isViewMode && !isCompanyDashboard) {
    officialShareAllocationColumns.push({
      Header: "Action",
      Cell: ({ row }) => {
        const isRowLoading =
          isLoading && selectedRows.includes(row.original.classOfShares);
        return (
          <div className="flex items-center space-x-2">
            {!resolvedFields.includes(row.original.classOfShares) && (
              <CommentsPopup
                handleSaveComment={handleSaveComment}
                formName="officialShareAllocation"
                fieldName={row.original.classOfShares}
                fieldLabel={row.original.classOfShares}
                comment={officialShareAllocationComments.find(
                  (comment) =>
                    comment.fieldName === row.original.classOfShares &&
                    currentUser?._id === comment.createdBy?._id
                )}
              >
                <span>Review</span>
                <PencilIcon className="inline w-4 h-4" />
              </CommentsPopup>
            )}
            <div className={`flex items-center justify-center pr-4`}>
              {resolvedFields.includes(row.original.classOfShares) ? (
                <button
                  className={`flex space-x-1 ${isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleInvalidateBtnClicked(row.original.classOfShares)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accepted</span>
                      <XIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className={`flex space-x-1 ${isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleResolveBtnClicked(row.original.classOfShares)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accept</span>
                      <CheckIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      },
    });
  }

  const updateOfficialDetails = async () => {
    try {
      const uploadedFiles = await handleFilesUpload(formData);
      let officialUpdateData = {};

      if (isAppointment) {
        officialUpdateData = {
          uploadChangeDocuments: {
            ...row?.original?.uploadChangeDocuments,
            appointment: {
              consentToAct: {
                ...row?.original?.uploadChangeDocuments?.appointment
                  ?.consentToAct,
                signedPdf: uploadedFiles?.consentToAct,
              },
              appointmentLetter: {
                ...row?.original?.uploadChangeDocuments?.appointment
                  ?.appointmentLetter,
                signedPdf: uploadedFiles?.appointmentLetter,
              },
            },
          },
        };
      }

      if (isChangeOfParticulars) {
        officialUpdateData = {
          uploadChangeDocuments: {
            ...row?.original?.uploadChangeDocuments,
            changeOfParticulars: {
              statutoryDeclaration: {
                ...row?.original?.uploadChangeDocuments?.changeOfParticulars
                  ?.statutoryDeclaration,
                signedPdf: uploadedFiles?.statutoryDeclaration,
              },
            },
          },
        };
      }

      if (isCessation) {
        officialUpdateData = {
          uploadChangeDocuments: {
            ...row?.original?.uploadChangeDocuments,
            cessation: {
              letterOfResignation: {
                ...row?.original?.uploadChangeDocuments?.cessation
                  ?.letterOfResignation,
                signedPdf: uploadedFiles?.letterOfResignation,
              },
              statutoryDeclaration: {
                ...row?.original?.uploadChangeDocuments?.cessation
                  ?.statutoryDeclaration,
                signedPdf: uploadedFiles?.statutoryDeclaration,
              },
            },
          },
        };
      }
      await dispatch(
        updateCompanyOfficialAsync({
          officialId: row.original._id,
          data: { ...officialUpdateData, idType: row.original.idType },
        })
      );
      toast("success", "Official details updated successfully.");
    } catch (error) {
      toast("error", error?.message ?? "Failed to update official details.");
    }
  };

  const validateErrors = () => {
    let errors = []
    if (isAppointment) {
      if (row?.original?.uploadChangeDocuments?.appointment
        ?.consentToAct && !formData.consentToAct) {
        errors.push("Signed consent to act documet is required")

      }
      if (row?.original?.uploadChangeDocuments?.appointment
        ?.appointmentLetter && !formData.appointmentLetter) {
        errors.push("Signed appointment letter documet is required")
      }
    }

    if (isChangeOfParticulars) {
      if (row?.original?.uploadChangeDocuments?.changeOfParticulars
        ?.statutoryDeclaration && !formData.statutoryDeclaration) {
        errors.push("Signed statutory declaration documet is required")

      }
    }

    if (isCessation) {
      if (row?.original?.uploadChangeDocuments?.cessation
        ?.letterOfResignation && !formData.letterOfResignation) {
        errors.push("Signed letter of resignation is required")
      }
      if (row?.original?.uploadChangeDocuments?.cessation
        ?.statutoryDeclaration && !formData.statutoryDeclaration) {
        errors.push("Signed statutory declaration documet is required")
      }
    }
    return errors
  }

  const updateOfficialChangeDocuments = async () => {
    const errors = validateErrors()
    if (errors.length > 0) {
      setFormError(errors)
      return false
    } else {
      setUpdatingOfficial(true)
      setFormError([]);
      await updateOfficialDetails()
      setUpdatingOfficial(false)
      return true
    }

  };

  const getNextStatus = (currentStatus) => {
    switch (currentStatus) {
      /** Handle for director approval docs */
      case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.CHANGES_SIGNATURE:
        return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_REVIEW;

      case Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_CS_CORRECTIONS:
        return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
          .REGISTRAR_CS_REVIEW_II;

      default:
        return Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW;
    }
  };

  useEffect(() => {
    if (transactions?.length > 0) {
      setTransaction(transactions?.find((trx) => trx._id === trxId));
    }
  }, [transactions]);

  /**
   * Handles updating official details with uploaded signed documents.
   */


  const updateTransactionStatus = async () => {
    const status = getNextStatus(transaction.status);
    await dispatch(
      updateTransaction({
        trxId,
        data: {
          status,
        },
      })
    );
  };

  const handleConfirmDetails = async () => {
    const nextRow = initialRows?.[row.index + 1] || null;
    if (isChangesSignature) {
      const isValid = await updateOfficialChangeDocuments()
      if (!isValid) return

      if (!nextRow) {
        updateTransactionStatus()
        history.push(`/companies/${companyId}/dashboard`)
      }
    }
    row.toggleRowExpanded();
    if (nextRow) {
      nextRow?.getToggleRowExpandedProps()?.onClick()
    }

  };

  const getLatestVersion = (fieldName) => {
    if (
      row.original?.[fieldName]?.versions &&
      row.original?.[fieldName]?.versions.length > 0
    ) {
      return row.original?.[fieldName]?.versions[
        row.original?.[fieldName]?.versions.length - 2
      ]; // we subtract 2 because -1 returns the current data
    } else {
      return row.original?.[fieldName];
    }
  };

  return (
    <tr className="bg-[#EDF0F5]">
      <td
        colSpan={visibleColumns.length}
        className={`px-9 ${isCompanyDashboard ? "pb-7" : ""}`}
      >
        <Tabs
          tabs={tabs}
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
        />
        {selectedTab.id === "appointer" && (
          <>
            {appointerComments && appointerComments.length > 0 && (
              <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                <CommentsList
                  comments={appointerComments}
                  toggleCommentStatus={false}
                />
                <p className="mt-6 text-sm text-primary">
                  Comments: {appointerComments.length} of{" "}
                  {appointerComments.length}
                </p>
              </div>
            )}
            <FormFieldsCard
              handleSaveComment={handleSaveComment}
              fields={[
                {
                  name: "appointers",
                  key: "Appointers",
                  value: Array.isArray(row.original?.appointers)
                    ? row.original.appointers
                      .map((item) => getFullName(item?.names) || "N/A")
                      .join(", ")
                    : row.original?.appointerNames ?? "N/A",
                },
              ]}
              resolvedFields={_get(row.original, "resolvedFields")}
              addReviewComment={isViewMode ? false : true}
              hideResolveAllCheckbox={isViewMode ? true : false}
              formName="appointers"
              formHelperKey={"individualOfficial"}
              updateEffectKey={companyOfficialEffectKey}
              updateEffectPath={officialSliceName}
              formId={_get(row.original, "_id")}
              comments={appointerComments}
            />
          </>
        )}
        {selectedTab.id === "generalInformation" &&
          (row.original?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.CESSATION ? (
            <div className="col-span-6 sm:col-span-3">
              <FormFieldsCard
                handleSaveComment={handleSaveComment}
                fields={getFormFields({
                  idType: row?.original?.idType,
                  formType: "cessation",
                  formValues: {
                    cessationDate: row?.original?.cessationDate,
                  },
                })}
                resolvedFields={_get(row.original, "resolvedFields")}
                addReviewComment={!isViewMode}
                hideResolveAllCheckbox={true}
                formName="cessationDate"
                formHelperKey={
                  currentTab === "stakeholderTab"
                    ? INDIVIDUALS.includes(row?.original?.idType)
                      ? "individualOfficial"
                      : "corporateOfficial"
                    : "beneficialOwner"
                }
                updateEffectKey={companyOfficialEffectKey}
                updateEffectPath={`${officialSliceName}`}
                formId={_get(row.original, "_id")}
                comments={generalInformationComments.filter(
                  (comment) => comment.formName === "individualOfficial"
                )}
                changeType={row?.original?.changeType}
              />
            </div>
          ) : (
            <>
              {generalInformationComments &&
                generalInformationComments.length > 0 && (
                  <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                    <CommentsList
                      comments={generalInformationComments}
                      toggleCommentStatus={!isViewMode}
                    />
                    <p className="mt-6 text-sm text-primary">
                      Comments: {generalInformationComments.length} of{" "}
                      {generalInformationComments.length}
                    </p>
                  </div>
                )}
              {row?.original?.isShareCapitalOfficial ? (
                <FormFieldsCard
                  handleSaveComment={handleSaveComment}
                  fields={getFormFields({
                    idType: row?.original?.idType,
                    formType: "names",
                    formValues: getLatestVersion("names"),
                  })}
                  resolvedFields={_get(row.original.names, "resolvedFields")}
                  addReviewComment={!isViewMode}
                  hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                  formName="names"
                  formHelperKey="names"
                  updateEffectKey={companyOfficialEffectKey}
                  updateEffectPath={`${officialSliceName}.names`}
                  formId={_get(row.original.names, "_id")}
                  comments={generalInformationComments.filter(
                    (comment) => comment.formName === "names"
                  )}
                  showChangedFields={row?.original?.changedFields}
                  changedFields={getFormFields({
                    idType: row?.original?.idType,
                    formType: "names",
                    formValues: row?.original?.changedFields?.names,
                  })}
                  changeType={row?.original?.changeType}
                />
              ) : (
                <div>
                  {row.original.designation === "Joint Shareholder" && (
                    <FormFieldsCard
                      handleSaveComment={handleSaveComment}
                      fields={[
                        {
                          key: "Is Director",
                          name: "isDirector",
                          value: row.original?.isDirector ?? "N/A",
                        },
                      ]}
                      resolvedFields={_get(row.original, "resolvedFields")}
                      addReviewComment={!isViewMode}
                      hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                      formName="isDirector"
                      formHelperKey={
                        currentTab === "stakeholderTab"
                          ? "individualOfficial"
                          : "beneficialOwner"
                      }
                      updateEffectKey={companyOfficialEffectKey}
                      updateEffectPath={officialSliceName}
                      formId={_get(row.original, "_id")}
                      comments={generalInformationComments.filter(
                        (comment) => comment.formName === "isDirector"
                      )}
                    />
                  )}
                  <FormFieldsCard
                    handleSaveComment={handleSaveComment}
                    fields={getFormFields({
                      idType: row?.original?.idType,
                      formType: "names",
                      formValues: getLatestVersion("names"),
                    })}
                    resolvedFields={_get(row.original.names, "resolvedFields")}
                    addReviewComment={!isViewMode}
                    hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                    formName="names"
                    formHelperKey="names"
                    updateEffectKey={companyOfficialEffectKey}
                    updateEffectPath={`${officialSliceName}.names`}
                    formId={_get(row.original.names, "_id")}
                    comments={generalInformationComments.filter(
                      (comment) => comment.formName === "names"
                    )}
                    showChangedFields={row?.original?.changedFields}
                    changedFields={getFormFields({
                      idType: row?.original?.idType,
                      formType: "names",
                      formValues: row?.original?.changedFields?.names,
                    })}
                    changeType={row?.original?.changeType}
                  />
                  <FormFieldsCard
                    handleSaveComment={handleSaveComment}
                    fields={getFormFields({
                      idType: row?.original?.idType,
                      formType: "identificationDetails",
                      formValues: getLatestVersion("identificationDetails"),
                    })}
                    resolvedFields={_get(
                      row.original.identificationDetails,
                      "resolvedFields"
                    )}
                    addReviewComment={!isViewMode}
                    hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                    formName="identificationDetails"
                    formHelperKey="identificationDetails"
                    updateEffectKey={companyOfficialEffectKey}
                    updateEffectPath={`${officialSliceName}.identificationDetails`}
                    formId={_get(row.original.identificationDetails, "_id")}
                    comments={generalInformationComments.filter(
                      (comment) => comment.formName === "identificationDetails"
                    )}
                    files={row.original.fileUploads}
                    showChangedFields={row?.original?.changedFields}
                    changedFields={getFormFields({
                      idType: row?.original?.idType,
                      formType: "identificationDetails",
                      formValues:
                        row?.original?.changedFields?.identificationDetails,
                    })}
                    changeType={row?.original?.changeType}
                  />
                  <FormFieldsCard
                    handleSaveComment={handleSaveComment}
                    fields={getFormFields({
                      idType: row?.original?.idType,
                      formType: "appointment",
                      formValues: {
                        appointmentDate: row?.original?.appointmentDate,
                      },
                    })}
                    resolvedFields={_get(row.original, "resolvedFields")}
                    addReviewComment={!isViewMode}
                    hideResolveAllCheckbox={true}
                    formName="appointmentDate"
                    formHelperKey={
                      currentTab === "stakeholderTab"
                        ? INDIVIDUALS.includes(row?.original?.idType)
                          ? "individualOfficial"
                          : "corporateOfficial"
                        : "beneficialOwner"
                    }
                    updateEffectKey={companyOfficialEffectKey}
                    updateEffectPath={`${officialSliceName}`}
                    formId={_get(row.original, "_id")}
                    comments={generalInformationComments.filter(
                      (comment) => comment.formName === "individualOfficial"
                    )}
                    changeType={row?.original?.changeType}
                  />
                  {[
                    "Local Company",
                    "Foreign Company",
                    "Other e.g Parastatals",
                  ].includes(row?.original?.idType) && (
                      <FormFieldsCard
                        handleSaveComment={handleSaveComment}
                        fields={getFormFields({
                          idType: row?.original?.idType,
                          formType: "contactPersonInfo",
                          formValues: getLatestVersion("identificationDetails"),
                        })}
                        resolvedFields={_get(
                          row.original.identificationDetails,
                          "resolvedFields"
                        )}
                        addReviewComment={!isViewMode}
                        hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                        formName="identificationDetails"
                        formHelperKey="identificationDetails"
                        updateEffectKey={companyOfficialEffectKey}
                        updateEffectPath={`${officialSliceName}.identificationDetails`}
                        formId={_get(row.original.identificationDetails, "_id")}
                        comments={generalInformationComments.filter(
                          (comment) =>
                            comment.formName === "identificationDetails"
                        )}
                        showChangedFields={row?.original?.changedFields}
                        changedFields={getFormFields({
                          idType: row?.original?.idType,
                          formType: "contatPersonInfo",
                          formValues:
                            row?.original?.changedFields?.identificationDetails,
                        })}
                        changeType={row?.original?.changeType}
                      />
                    )}
                </div>
              )}
            </>
          ))}
        {selectedTab.id === "residentialDetails" && (
          <>
            {residentialDetailsComments &&
              residentialDetailsComments.length > 0 && (
                <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                  <CommentsList
                    comments={residentialDetailsComments}
                    toggleCommentStatus={!isViewMode}
                  />
                  <p className="mt-6 text-sm text-primary">
                    Comments: {residentialDetailsComments.length} of{" "}
                    {residentialDetailsComments.length}
                  </p>
                </div>
              )}
            {row.original?.isShareCapitalOfficial ? (
              <FormFieldsCard
                handleSaveComment={handleSaveComment}
                fields={getFormFields({
                  idType:
                    row?.original?.isNonResident &&
                      row?.original?.isNonResident === "Yes"
                      ? "Foreigner"
                      : row?.original?.idType,
                  formType: "postalAddress",
                  formValues: getLatestVersion("postalAddress"),
                })}
                resolvedFields={_get(
                  row.original.postalAddress,
                  "resolvedFields"
                )}
                addReviewComment={!isViewMode}
                hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                formName="postalAddress"
                formHelperKey="postalAddress"
                updateEffectKey={companyOfficialEffectKey}
                updateEffectPath={`${officialSliceName}.postalAddress`}
                formId={_get(row.original.postalAddress, "_id")}
                comments={residentialDetailsComments.filter(
                  (comment) => comment.formName === "postalAddress"
                )}
                showChangedFields={row?.original?.changedFields}
                changedFields={getFormFields({
                  idType: row?.original?.idType,
                  formType: "postalAddress",
                  formValues: row?.original?.changedFields?.postalAddress,
                })}
                changeType={row?.original?.changeType}
              />
            ) : (
              <div>
                {row?.original?.idType === "Kenyan Citizen" && (
                  <FormFieldsCard
                    handleSaveComment={handleSaveComment}
                    fields={[
                      {
                        key: "Non-Resident",
                        name: "isNonResident",
                        value: row.original?.isNonResident ?? "N/A",
                      },
                    ]}
                    resolvedFields={_get(row.original, "resolvedFields")}
                    addReviewComment={!isViewMode}
                    hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                    formName="isNonResident"
                    formHelperKey={
                      currentTab === "stakeholderTab"
                        ? INDIVIDUALS.includes(row?.original?.idType)
                          ? "individualOfficial"
                          : "corporateOfficial"
                        : "beneficialOwner"
                    }
                    updateEffectKey={companyOfficialEffectKey}
                    updateEffectPath={officialSliceName}
                    formId={_get(row.original, "_id")}
                    comments={residentialDetailsComments.filter(
                      (comment) => comment.formName === "isNonResident"
                    )}
                  />
                )}
                <FormFieldsCard
                  handleSaveComment={handleSaveComment}
                  fields={getFormFields({
                    idType:
                      row?.original?.isNonResident &&
                        row?.original?.isNonResident === "Yes"
                        ? "Foreigner"
                        : row?.original?.idType,
                    formType: "postalAddress",
                    formValues: getLatestVersion("postalAddress"),
                  })}
                  resolvedFields={_get(
                    row.original.postalAddress,
                    "resolvedFields"
                  )}
                  addReviewComment={!isViewMode}
                  hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                  formName="postalAddress"
                  formHelperKey="postalAddress"
                  updateEffectKey={companyOfficialEffectKey}
                  updateEffectPath={`${officialSliceName}.postalAddress`}
                  formId={_get(row.original.postalAddress, "_id")}
                  comments={residentialDetailsComments.filter(
                    (comment) => comment.formName === "postalAddress"
                  )}
                  showChangedFields={row?.original?.changedFields}
                  changedFields={getFormFields({
                    idType: row?.original?.idType,
                    formType: "postalAddress",
                    formValues: row?.original?.changedFields?.postalAddress,
                  })}
                  changeType={row?.original?.changeType}
                />
                <FormFieldsCard
                  handleSaveComment={handleSaveComment}
                  fields={getFormFields({
                    idType: row?.original?.idType,
                    formType: "electronicAddress",
                    formValues: getLatestVersion("electronicAddress"),
                  })}
                  resolvedFields={_get(
                    row.original.electronicAddress,
                    "resolvedFields"
                  )}
                  addReviewComment={!isViewMode}
                  hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                  formName="electronicAddress"
                  formHelperKey="electronicAddress"
                  updateEffectKey={companyOfficialEffectKey}
                  updateEffectPath={`${officialSliceName}.electronicAddress`}
                  formId={_get(row.original.electronicAddress, "_id")}
                  comments={residentialDetailsComments.filter(
                    (comment) => comment.formName === "electronicAddress"
                  )}
                  showChangedFields={row?.original?.changedFields}
                  changedFields={getFormFields({
                    idType: row?.original?.idType,
                    formType: "electronicAddress",
                    formValues: row?.original?.changedFields?.electronicAddress,
                  })}
                  changeType={row?.original?.changeType}
                />
                <FormFieldsCard
                  handleSaveComment={handleSaveComment}
                  fields={getFormFields({
                    idType:
                      row?.original?.isNonResident &&
                        row?.original?.isNonResident === "Yes"
                        ? "Foreigner"
                        : row?.original?.idType,
                    formType: "residentialAddressDetails",
                    formValues: getLatestVersion("residentialAddressDetails"),
                  })}
                  resolvedFields={_get(
                    row.original.residentialAddressDetails,
                    "resolvedFields"
                  )}
                  addReviewComment={!isViewMode}
                  hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
                  formName="residentialAddressDetails"
                  formHelperKey="residentialAddressDetails"
                  updateEffectKey={companyOfficialEffectKey}
                  updateEffectPath={`${officialSliceName}.residentialAddressDetails`}
                  formId={_get(row.original.residentialAddressDetails, "_id")}
                  comments={residentialDetailsComments.filter(
                    (comment) =>
                      comment.formName === "residentialAddressDetails"
                  )}
                  showChangedFields={row?.original?.changedFields}
                  changedFields={getFormFields({
                    idType: row?.original?.idType,
                    formType: "residentialAddressDetails",
                    formValues:
                      row?.original?.changedFields?.residentialAddressDetails,
                  })}
                  changeType={row?.original?.changeType}
                />
              </div>
            )}
          </>
        )}
        {selectedTab.id === "officialShareAllocation" && (
          <>
            {officialShareAllocationComments &&
              officialShareAllocationComments.length > 0 && (
                <div className="px-8 pt-5 pb-6 my-6 bg-white">
                  <CommentsList
                    comments={officialShareAllocationComments}
                    toggleCommentStatus={!isViewMode}
                  />
                  <p className="mt-6 text-sm text-primary">
                    Comments: {officialShareAllocationComments.length} of{" "}
                    {officialShareAllocationComments.length}
                  </p>
                </div>
              )}
            {row.original.sharesAllotted?.length > 0 ? (
              <div className="w-full bg-white shadow">
                <div className="w-full overflow-x-auto">
                  <ReactTable
                    columns={officialShareAllocationColumns}
                    data={row.original.sharesAllotted}
                    dataRowStyle="h-12 text-sm leading-none text-gray-700 bg-white border-t border-b border-gray-200 hover:bg-gray-50"
                  />
                </div>

                {/* Beneficial Ownership Component below the table */}
                {/* <div className="mt-2 p-4">
                  <h3 className="mb-4 text-md font-semibold">
                    Beneficial Ownership Details
                  </h3>
                  <Formik
                    enableReinitialize
                    initialValues={{
                      beneficialOwnershipForm:
                        row.original.beneficialOwnershipForm || {},
                    }}
                    onSubmit={() => { }}
                  >
                    {(formikProps) => {
                      return (
                        <OfficialsBeneficialOwnershipComponent
                          field={{
                            name: "beneficialOwnershipForm",
                            value: row.original.beneficialOwnershipForm || {},
                          }}
                          form={formikProps}
                          corporateIdType={
                            !INDIVIDUALS.includes(row?.original?.idType)
                          }
                          isBOTab={false}
                          disableChangeEffect={true}
                          selectedShareholder={row.original}
                          disableDirectFields={false}
                          isReportable={false}
                          isInManualUpdate={false}
                          isinChangesList={true}
                          transactions={transactions}
                          row={row}
                          disabled={false}
                          resolvedFields={_get(
                            row.original.beneficialOwnershipForm,
                            "resolvedFields",
                            []
                          )}
                        />
                      );
                    }}
                  </Formik>
                </div> */}
              </div>
            ) : (
              <p className="mt-4 text-center">No data available</p>
            )}
            {!isCompanyDashboard &&
              !isViewMode &&
              row.original.sharesAllotted?.length > 0 && (
                <div className="flex items-center mt-2">
                  <input
                    aria-describedby="comments-description"
                    type="checkbox"
                    className="mr-2 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    onChange={handleAllFieldsStatus}
                    checked={allFieldsResolved}
                  />
                  <p className="flex items-center text-sm font-semibold">
                    Click this checkbox to{" "}
                    {allFieldsResolved ? "disapprove" : "approve"} all entries
                    if they are {allFieldsResolved ? "incorrect" : "correct"}{" "}
                    then proceed to next step
                  </p>
                </div>
              )}
          </>
        )}
        {selectedTab.id === "officialBeneficialOwnership" && (
          <>
            {officialBeneficialOwnershipComments &&
              officialBeneficialOwnershipComments.length > 0 && (
                <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                  <CommentsList
                    comments={officialBeneficialOwnershipComments}
                    toggleCommentStatus={!isViewMode}
                  />
                  <p className="mt-6 text-sm text-primary">
                    Comments: {officialBeneficialOwnershipComments.length} of{" "}
                    {officialBeneficialOwnershipComments.length}
                  </p>
                </div>
              )}
            <FormFieldsCard
              handleSaveComment={handleSaveComment}
              fields={getBeneficialOwnershipFormFields({
                beneficialOwnershipForm: row.original.beneficialOwnershipForm,
                reviewTab: true,
                beneficialOwner: row.original,
                beneficialOwners,
              })}
              resolvedFields={_get(
                row.original.beneficialOwnershipForm,
                "resolvedFields"
              )}
              addReviewComment={!isViewMode}
              hideResolveAllCheckbox={isCompanyDashboard || isViewMode}
              formName="beneficialOwnershipForm"
              formHelperKey="beneficialOwnershipForm"
              updateEffectKey={companyOfficialEffectKey}
              updateEffectPath={`${officialSliceName}.beneficialOwnershipForm`}
              formId={_get(row.original.beneficialOwnershipForm, "_id")}
              comments={officialBeneficialOwnershipComments}
            />
          </>
        )}
        {selectedTab.id === "uploadDocuments" && (
          <>
            {fileUploadComments && fileUploadComments.length > 0 && (
              <div className="px-8 pt-5 pb-6 mt-6 bg-white">
                <CommentsList
                  comments={fileUploadComments}
                  toggleCommentStatus={!isViewMode}
                />
                <p className="mt-6 text-sm text-primary">
                  Comments: {fileUploadComments.length} of{" "}
                  {fileUploadComments.length}
                </p>
              </div>
            )}
            <div className="px-8 pt-5 pb-6 mt-6 bg-white">
              <h3 className="mb-2 text-lg">Uploaded incorporation forms</h3>
              <Formik
                enableReinitialize
                initialValues={{
                  fileUploads: row.original.fileUploads,
                }}
                onSubmit={() => null}
              >
                {() => {
                  return (
                    <div className="pl-4">
                      <Field
                        name="fileUploads"
                        component={FileUploadForm}
                        documentData={
                          row.original.designation === "Company Secretary"
                            ? getCompanySecretaryDocumentData(
                              row.original.idType
                            )
                            : getDocumentData(row.original.idType)
                        }
                        resolvedFields={_get(row.original, "resolvedFields")}
                        onlyPreview
                        showDocumentLabel
                        addReviewComment={!isViewMode}
                        hideResolveAllCheckbox={
                          isCompanyDashboard || isViewMode
                        }
                        handleSaveComment={handleSaveComment}
                        formName="uploadDocuments"
                        formHelperKey={
                          currentTab === "stakeholderTab"
                            ? INDIVIDUALS.includes(row?.original?.idType)
                              ? "individualOfficial"
                              : "corporateOfficial"
                            : "beneficialOwner"
                        }
                        updateEffectKey={companyOfficialEffectKey}
                        updateEffectPath={officialSliceName}
                        formId={_get(row.original, "_id")}
                        resolveAllFieldsCheckbox
                        fileUploadComments={fileUploadComments}
                        changedFields={row.original?.changedFields}
                        changeType={row?.original?.changeType}
                      />
                    </div>
                  );
                }}
              </Formik>
            </div>
          </>
        )}
        {selectedTab.id === "changedDocuments" && (
          <ChangeDocuments
            row={row}
            isViewMode={isViewMode}
            handleSaveComment={handleSaveComment}
            companyOfficialEffectKey={companyOfficialEffectKey}
            officialSliceName={officialSliceName}
            isCompanyDashboard={isCompanyDashboard}
            changeDocumentsComments={changeDocumentsComments}
            isReviewMode
          />
        )}

        {selectedTab.id === "eSignature" && <ESignature />}

        {selectedTab.id === "changedDocumentsSignature" && (
          <ChangeDocumentSignedUnsigned
            row={row}
            setFormData={setFormData}
            formError={formError}
          />
        )}

        {!isCompanyDashboard && (
          <div className="flex justify-between">
            {!isFirstStep && (
              <button
                onClick={goToPrevStep}
                className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
              >
                <span className="flex items-center mr-4 h-7">
                  <ChevronLeftIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
                <p className="font-semibold">Go back</p>
              </button>
            )}
            {isLastStep ? (
              <>
                {(
                  <Button
                    onClick={() => handleConfirmDetails()}
                    className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded bg-green-focused mt-7"
                    isLoading={updatingOfficial}
                    loadingText={"Updating..."}
                  >
                    <p>
                      {isViewMode
                        ? "Close"
                        : "Confirm details"}
                    </p>
                    {!isViewMode && (
                      <span className="flex items-center ml-4 text-white h-7">
                        <ChevronRightIcon
                          className="flex-shrink-0 w-5 h-5"
                          aria-hidden="true"
                        />
                      </span>
                    )}
                  </Button>
                )}
              </>
            ) : (
              <button
                onClick={goToNextStep}
                className="flex items-start float-right py-2 ml-auto font-semibold rounded text-primary mt-7"
              >
                <p className="font-semibold">Next step </p>
                <span className="flex items-center ml-4 h-7">
                  <ChevronRightIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

export default StakeholderSubRow;
